import React, { createContext, useContext, useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";
import {
  Instagram,
  Linkedin,
  Twitter,
  Facebook,
  Youtube,
  Globe,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Import your modal component (adjust path if needed)
import ImageCropperModal from "../components/profile/ImageCropperModal";

const ProfileFormContext = createContext(null);

const SOCIAL_PLATFORMS = [
  { key: "instagram", name: "Instagram", icon: Instagram, color: "#E4405F" },
  { key: "linkedin", name: "LinkedIn", icon: Linkedin, color: "#0077B5" },
  { key: "twitter", name: "Twitter", icon: Twitter, color: "#1DA1F2" },
  { key: "facebook", name: "Facebook", icon: Facebook, color: "#1877F2" },
  { key: "youtube", name: "YouTube", icon: Youtube, color: "#FF0000" },
  { key: "website", name: "Website", icon: Globe, color: "#6B7280" },
];

const DEFAULT_OPEN_ACCORDIONS = ["template-layout"];
const MAX_IMAGES = 4;
const MAX_FILE_SIZE_MB = 1;

export const ProfileFormProvider = ({ children, initialData = null }) => {
  const navigate = useNavigate();

  const [profileId, setProfileId] = useState(initialData?._id || null);

  // --- PROFILE IMAGE STATE ---
  const [profileImageUrl, setProfileImageUrl] = useState(
    initialData?.profileImageUrl
      ? `${API_BASE_URL}${initialData.profileImageUrl}`
      : "/profile-placeholder.png"
  );
  const [profileImageFile, setProfileImageFile] = useState(null);
  const profilePicInputRef = useRef(null);

  // --- COVER IMAGE STATE ---
  const [coverImageUrl, setCoverImageUrl] = useState(
    initialData?.coverImageUrl
      ? `${API_BASE_URL}${initialData.coverImageUrl}`
      : "/cover-placeholder.png"
  );
  const [coverImageFile, setCoverImageFile] = useState(null);
  const coverPicInputRef = useRef(null);

  // --- BASIC INFO STATE ---
  const [name, setName] = useState(initialData?.name || "");
  const [title, setTitle] = useState(initialData?.title || "");
  const [company, setCompany] = useState(initialData?.company || "");
  const [jobTitle, setJobTitle] = useState(initialData?.jobTitle || "");

  // --- CROP MODAL STATE ---
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null); // data URL for modal
  const [cropAspect, setCropAspect] = useState(1); // 1 = square (profile), 16/9 = cover
  const [cropTarget, setCropTarget] = useState(null); // "profile" | "cover" | "gallery-<idx>"
  const [pendingFileName, setPendingFileName] = useState(null); // optional: keep original name

  const openCropper = (file, target = "profile") => {
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result); // base64 data URL
      setCropTarget(target);
      setCropAspect(target === "profile" ? 1 : 16 / 9);
      setPendingFileName(file.name || `${target}-${Date.now()}`);
      setIsCropModalOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleProfilePicUpload = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    openCropper(file, "profile");
  };

  const handleCoverPicUpload = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    openCropper(file, "cover");
  };

  // --- GALLERY STATE (Incorporating Deletion Tracking) ---
  const [openAccordion, setOpenAccordion] = useState(DEFAULT_OPEN_ACCORDIONS);
  
  // Gallery URLs (server path or local blob URL)
  const [galleryImages, setGalleryImages] = useState(
    (initialData?.galleryImages || []).map((path) => `${API_BASE_URL}${path}`)
  );

  // Gallery Files (File objects for new uploads, null for existing server images)
  const [galleryImageFiles, setGalleryImageFiles] = useState(
    Array.from({ length: galleryImages.length }, () => null)
  );
  
  // NEW: Tracks which server URLs need to be deleted
  const [deletedGalleryImages, setDeletedGalleryImages] = useState([]);

  const galleryInputRef = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleGalleryFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remainingSlots = MAX_IMAGES - galleryImages.length;
    const filesToAdd = files.slice(0, remainingSlots);

    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error(`Each gallery image must be under ${MAX_FILE_SIZE_MB} MB`);
        return;
      }
    }

    const previews = filesToAdd.map((f) => URL.createObjectURL(f));
    
    // Add new previews and new file objects
    setGalleryImages((prev) => [...prev, ...previews]);
    setGalleryImageFiles((prev) => [...prev, ...filesToAdd]);
    
    // Clear input value to allow re-uploading the same file
    e.target.value = null;
  };

  
const removeGalleryImage = (index) => {
  setGalleryImages((prevImages) => {
    const img = prevImages[index];

    // Track deleted server image
    if (img && !img.startsWith("blob:")) {
      setDeletedGalleryImages((prev) => [...prev, img]);
    }

    // Cleanup blob URL
    if (img?.startsWith("blob:")) {
      try { URL.revokeObjectURL(img); } catch {}
    }

    const newImages = prevImages.filter((_, i) => i !== index);

    // Fix index safely
    setCurrentImageIndex((prevIdx) => {
      if (newImages.length === 0) return 0;
      if (prevIdx >= newImages.length) return 0;
      return prevIdx;
    });

    return newImages;
  });

  // Sync the files array
  setGalleryImageFiles((prevFiles) =>
    prevFiles.filter((_, i) => i !== index)
  );
};


  // RESTORED GALLERY NAVIGATION FUNCTIONS
  const nextImage = () =>
    galleryImages.length > 0 &&
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);

  const prevImage = () =>
    galleryImages.length > 0 &&
    setCurrentImageIndex(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length
    );

  // --- CONTACT STATE ---
  const [phones, setPhones] = useState(
    initialData?.phones ?? [{ id: Date.now(), type: "Mobile", number: "" }]
  );

  const [emails, setEmails] = useState(
    initialData?.emails ?? [{ id: Date.now(), type: "Work", address: "" }]
  );

  // --- CONTACT HELPERS (Restored Original Logic) ---
  const updateContact = (setter, id, field, value) => {
    setter((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const addContact = (setter, newItem) => {
    setter((prev) => [...prev, { id: Date.now(), ...newItem }]);
  };

  const removeContact = (setter, id) => {
    setter((prev) => prev.filter((item) => item.id !== id));
  };

  const [website, setWebsite] = useState(initialData?.website || "");
  const [address, setAddress] = useState(initialData?.address || "");
  const [addressLink, setAddressLink] = useState(
    initialData?.addressLink || ""
  );

  // --- SOCIALS STATE & MODAL ---
  const [socials, setSocials] = useState(initialData?.socials ?? []);
  const [isSocialModalOpen, setSocialModalOpen] = useState(false);
  const [socialsEnableText, setSocialsEnableText] = useState(true); // RESTORED
  const [platformToAdd, setPlatformToAdd] = useState(null);

  // RESTORED SOCIAL MODAL HELPERS
  const openSocialModal = (platformKey) => {
    if (!platformKey) return;
    setPlatformToAdd(platformKey);
    setSocialModalOpen(true);
  };

  const closeSocialModal = () => {
    setSocialModalOpen(false);
    setPlatformToAdd(null);
  };

  // --- CROPPING APPLY HANDLER (Restored Revoke Logic) ---
  const applyCroppedImage = (blob) => {
    if (!blob) return;

    // Create a File (preserves filename when uploading if needed)
    const filename = pendingFileName || `image-${Date.now()}.jpg`;
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

    // Helper to revoke old blob urls
    const revokeIfBlobUrl = (url) => {
      try {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      } catch (e) {
        // ignore
      }
    };

    const url = URL.createObjectURL(file);

    if (cropTarget === "profile") {
      revokeIfBlobUrl(profileImageUrl);
      setProfileImageFile(file);
      setProfileImageUrl(url);
    } else if (cropTarget === "cover") {
      revokeIfBlobUrl(coverImageUrl);
      setCoverImageFile(file);
      setCoverImageUrl(url);
    } else if (cropTarget && cropTarget.startsWith("gallery-")) {
      const idx = Number(cropTarget.split("-")[1]);

      setGalleryImages((prev) => {
        const next = [...prev];
        // revoke previous if blob (restored original revoke)
        try {
          if (next[idx] && next[idx].startsWith("blob:"))
            URL.revokeObjectURL(next[idx]);
        } catch (e) {}
        next[idx] = url;
        return next;
      });

      setGalleryImageFiles((prev) => {
        const next = [...prev];
        // Ensure array is long enough for the index (handle update of a new image index)
        while (next.length <= idx) next.push(null); 
        next[idx] = file;
        return next;
      });
    }

    // cleanup crop modal related state
    setIsCropModalOpen(false);
    setCropImageSrc(null);
    setCropTarget(null);
    setPendingFileName(null);
  };

  /* THEME & UI */
  const [template, setTemplate] = useState(
    initialData?.theme?.template || "template1"
  );

  const [showGallery, setShowGallery] = useState(
    initialData?.theme?.showGallery ?? true
  );

  const [showSocials, setShowSocials] = useState(
    initialData?.theme?.showSocials ?? true
  );

  const [primaryColor, setPrimaryColor] = useState(
    initialData?.theme?.primaryColor || "#1A1A1A"
  );

  const [accentColor, setAccentColor] = useState(
    initialData?.theme?.accentColor || "#FF4F00"
  );

  const [iconColor, setIconColor] = useState(
    initialData?.theme?.iconColor || "#FFFFFF"
  );

  const [titleTextColor, setTitleTextColor] = useState(
    initialData?.theme?.titleTextColor || "#FFFFFF"
  );

  const [bioTextColor, setBioTextColor] = useState(
    initialData?.theme?.bioTextColor || "#E5E7EB"
  );

  const [fontFamily, setFontFamily] = useState(
    initialData?.theme?.fontFamily || "'Inter', sans-serif"
  );

  const [isGalleryModalOpen, setGalleryModalOpen] = useState(false);
  const [isMobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vcfUrl, setVcfUrl] = useState(initialData?.vcfUrl || "#");


  // --- SAVE HANDLER (Merged Logic) ---
  const handleSave = async () => {
    if (!name.trim()) return toast.error("Name is required");

    // Re-added social validation (removed by LLM)
    if (showSocials && socials.length > 0) {
      for (const s of socials) {
        if (!s.platform || !s.link) {
          return toast.error("Each social link must include a platform and link");
        }
      }
    }

    const formData = new FormData();
    
    // Helper to strip API_BASE_URL (New helper from updated code)
    const normalize = (url) =>
      url?.startsWith(API_BASE_URL) ? url.replace(API_BASE_URL, "") : url;


    const data = {
      theme: {
        template,
        showGallery,
        showSocials,
        primaryColor,
        accentColor,
        iconColor,
        titleTextColor,
        bioTextColor,
        fontFamily,
      },
      name,
      title,
      company,
      jobTitle,
      phones,
      emails,
      website,
      address,
      addressLink,
      socials,
      
      // Existing Gallery (URLs we want to keep) - Mapped to relative paths
      existingGallery: galleryImages
        .filter((img) => typeof img === "string" && !img.startsWith("blob:"))
        .map(normalize),
        
      // NEW: Deleted Gallery (URLs we want the backend to remove) - Mapped to relative paths
      deletedGalleryImages: deletedGalleryImages.map(normalize),
    };

    if (!profileId) {
      const baseName =
        name?.trim()?.toLowerCase().replace(/\s+/g, "-") || "user";

      data.nfcLink = `${baseName}-${Math.random().toString(36).substring(2, 7)}`;
    }

    formData.append("data", JSON.stringify(data));

    if (profileImageFile) {
      formData.append("profileImage", profileImageFile);
    }

    if (coverImageFile) {
      formData.append("coverImage", coverImageFile);
    }

    // Only append actual file objects (ignores null placeholders for existing server images)
    galleryImageFiles.forEach((file) => {
      if (file instanceof File) {
          formData.append("galleryImages", file);
      }
    });

    try {
      setLoading(true);

      const method = profileId ? "PUT" : "POST";
      const url = profileId
        ? `${API_BASE_URL}/api/profile/${profileId}`
        : `${API_BASE_URL}/api/profile`;

      const response = await fetch(url, {
        method,
        body: formData,
        credentials: "include",
      });
      
      // Improved error handling from updated code
      const contentType = response.headers.get("content-type") || "";
      const responseBody = contentType.includes("application/json")
        ? await response.json()
        : { message: await response.text() };

      if (!response.ok) {
        return toast.error(responseBody?.message || "Error saving profile");
      }

      toast.success(profileId ? "Profile updated successfully!" : "Profile saved successfully!");

      const p = responseBody?.profile || responseBody;
      
      // Update URLs from server response (mapping relative paths to full URLs)
      setProfileImageUrl(
        p.profileImageUrl
          ? `${API_BASE_URL}${p.profileImageUrl}`
          : "/profile-placeholder.png"
      );

      setCoverImageUrl(
        p.coverImageUrl
          ? `${API_BASE_URL}${p.coverImageUrl}`
          : "/cover-placeholder.png"
      );

      const newGallery = (p.galleryImages || []).map((path) => `${API_BASE_URL}${path}`);
      setGalleryImages(newGallery);

      // RESET LOCAL STATE AND DELETION TRACKING
      setProfileImageFile(null);
      setCoverImageFile(null);
      setGalleryImageFiles(Array.from({ length: newGallery.length }, () => null)); // Reset files array length
      setDeletedGalleryImages([]); // IMPORTANT: Clear deletion tracking

      if (profileId) {
        setTimeout(() => navigate("/admin/profiles"), 500);
      } else {
        const newID = p._id || p.id;
        navigate(`/profile-link/${newID}`, {
          state: { action: "created" },
        });
      }
    } catch (err) {
      console.error("🔥 handleSave error:", err);
      toast.error("Error saving profile");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cleanup any blob URLs when provider unmounts (Original logic restored)
  useEffect(() => {
    return () => {
      try {
        if (profileImageUrl && profileImageUrl.startsWith("blob:"))
          URL.revokeObjectURL(profileImageUrl);
        if (coverImageUrl && coverImageUrl.startsWith("blob:"))
          URL.revokeObjectURL(coverImageUrl);
        galleryImages.forEach((g) => {
          if (g && g.startsWith && g.startsWith("blob:")) URL.revokeObjectURL(g);
        });
      } catch (e) {
        // noop
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProfileFormContext.Provider
      value={{
        // images
        profileImageUrl,
        setProfileImageUrl,
        profilePicInputRef,
        handleProfilePicUpload,
        profileImageFile,

        coverImageUrl,
        setCoverImageUrl,
        coverImageFile,
        coverPicInputRef,
        handleCoverPicUpload,

        // basic info
        name, setName,
        title, setTitle,
        company, setCompany,
        jobTitle, setJobTitle,

        // contact (RESTORED CONTACT HELPERS)
        phones, setPhones,
        emails, setEmails,
        updateContact,
        addContact,
        removeContact,

        website, setWebsite,
        address, setAddress,
        addressLink, setAddressLink,

        // socials (RESTORED MODAL HELPERS)
        socials, setSocials,
        isSocialModalOpen, setSocialModalOpen,
        socialsEnableText, setSocialsEnableText,
        platformToAdd, setPlatformToAdd,
        openSocialModal,
        closeSocialModal,

        // gallery (RESTORED NAVIGATION)
        galleryImages,
        galleryImageFiles,
        handleGalleryFileSelect,
        removeGalleryImage,
        galleryInputRef,
        currentImageIndex,
        setCurrentImageIndex, // Exported setter for external control
        nextImage,
        prevImage,
        isGalleryModalOpen, setGalleryModalOpen,

        // theme
        template, setTemplate,
        showGallery, setShowGallery,
        showSocials, setShowSocials,
        primaryColor, setPrimaryColor,
        accentColor, setAccentColor,
        iconColor, setIconColor,
        titleTextColor, setTitleTextColor,
        bioTextColor, setBioTextColor,
        fontFamily, setFontFamily,

        // ui
        isMobilePreviewOpen, setMobilePreviewOpen,
        loading,
        handleSave,
        vcfUrl, setVcfUrl,

        // misc (RESTORED ACCORDION STATE)
        SOCIAL_PLATFORMS,
        MAX_IMAGES,
        openAccordion, 
        setOpenAccordion,

        // cropping
        isCropModalOpen,
        setIsCropModalOpen,
        cropImageSrc,
        cropAspect,
        openCropper,
        applyCroppedImage,
        pendingFileName,
      }}
    >
      {/* Image cropper modal is mounted once here and controlled by context state */}
      <ImageCropperModal
        open={isCropModalOpen}
        onClose={() => {
          setIsCropModalOpen(false);
          setCropImageSrc(null);
          setCropTarget(null);
          setPendingFileName(null);
        }}
        imageSrc={cropImageSrc}
        aspect={cropAspect}
        onCropComplete={applyCroppedImage}
      />

      {children}
    </ProfileFormContext.Provider>
  );
};

export const useProfileForm = () => {
  const context = useContext(ProfileFormContext);
  if (!context)
    throw new Error("useProfileForm must be used within ProfileFormProvider");
  return context;
};